import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { fetchPublicSettings } from '@/services/settings'
import {
  isStoreLaunched,
  resolveComingSoonSchedule,
  type ComingSoonSchedule,
} from '@/lib/comingSoon'
import type { StoreSettings } from '@/types'

interface StoreGateContextValue {
  status: 'loading' | 'ready'
  settings: StoreSettings | null
  comingSoonEnabled: boolean
  /** True once the scheduled launch moment has passed. */
  storeLaunched: boolean
  /** Gate is active only before the scheduled launch moment. */
  showComingSoonPage: boolean
  schedule: ComingSoonSchedule
  refresh: () => Promise<void>
  /** Keep the coming-soon route during the live countdown celebration. */
  beginLaunchCelebration: () => void
  /** Call after the live launch celebration finishes to open the storefront. */
  endLaunchCelebration: () => void
}

const StoreGateContext = createContext<StoreGateContextValue | null>(null)

export function StoreGateProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<StoreSettings | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready'>('loading')
  const [now, setNow] = useState(() => Date.now())
  const [launchCelebration, setLaunchCelebration] = useState(false)
  const [celebrationEnded, setCelebrationEnded] = useState(false)
  const wasWaitingAtMountRef = useRef<boolean | null>(null)

  const load = async () => {
    try {
      const data = await fetchPublicSettings()
      setSettings(data)
    } catch {
      setSettings(null)
    } finally {
      setStatus('ready')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const comingSoonEnabled = settings?.comingSoonEnabled === true
  const schedule = useMemo(
    () => resolveComingSoonSchedule(settings ?? {}),
    [settings?.comingSoonLaunchAt, settings?.comingSoonProgressStartAt]
  )
  const storeLaunched = isStoreLaunched(schedule, now)

  if (wasWaitingAtMountRef.current === null && status === 'ready') {
    wasWaitingAtMountRef.current =
      comingSoonEnabled && !isStoreLaunched(schedule, Date.now())
  }

  /** Keep the coming-soon page up through "We're Live!" for visitors who waited. */
  const shouldShowCelebration =
    !celebrationEnded &&
    (launchCelebration ||
      (wasWaitingAtMountRef.current === true &&
        storeLaunched &&
        comingSoonEnabled))

  useEffect(() => {
    if (!comingSoonEnabled || celebrationEnded || storeLaunched) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [
    comingSoonEnabled,
    celebrationEnded,
    storeLaunched,
    schedule.launchDate.getTime(),
  ])

  const beginLaunchCelebration = useCallback(() => {
    setLaunchCelebration(true)
  }, [])

  const endLaunchCelebration = useCallback(() => {
    setLaunchCelebration(false)
    setCelebrationEnded(true)
    wasWaitingAtMountRef.current = false
  }, [])

  const value = useMemo<StoreGateContextValue>(() => {
    return {
      status,
      settings,
      comingSoonEnabled,
      storeLaunched,
      showComingSoonPage:
        comingSoonEnabled && (!storeLaunched || shouldShowCelebration),
      schedule,
      refresh: load,
      beginLaunchCelebration,
      endLaunchCelebration,
    }
  }, [
    settings,
    status,
    comingSoonEnabled,
    storeLaunched,
    shouldShowCelebration,
    schedule,
    beginLaunchCelebration,
    endLaunchCelebration,
  ])

  return (
    <StoreGateContext.Provider value={value}>{children}</StoreGateContext.Provider>
  )
}

export function useStoreGate() {
  const ctx = useContext(StoreGateContext)
  if (!ctx) {
    throw new Error('useStoreGate must be used within StoreGateProvider')
  }
  return ctx
}
